import { createSlice, createSelector } from '@reduxjs/toolkit';

// Plan-based permissions
const PLAN_PERMISSIONS = {
  '8-Day Freemium': {
    maxAgents: 1,
    allowedFeatures: ['agents', 'integrations', 'calendar'],
    restrictedFeatures: ['analytics', 'testing', 'mcp', 'cost-calculator'],
    allowedPages: ['/', '/agents', '/integrations', '/calendar', '/settings', '/payment', '/dashboard'],
    restrictedPages: ['/analytics', '/testing', '/mcp', '/cost-calculator'],
  },
  // Production plan name in `plans` table
  BYOK: {
    maxAgents: -1,
    allowedFeatures: [
      'agents',
      'integrations',
      'calendar',
      'analytics',
      'testing',
      'mcp',
      'cost-calculator',
      'knowledge',
      'sales',
    ],
    restrictedFeatures: [],
    allowedPages: [
      '/',
      '/dashboard',
      '/agents',
      '/integrations',
      '/calendar',
      '/analytics',
      '/testing',
      '/mcp',
      '/cost-calculator',
      '/settings',
      '/payment',
      '/knowledge',
      '/sales',
      '/sales/runs',
      '/workflows',
    ],
    restrictedPages: [],
  },
  'Standard (Your AI + TTS)': {
    maxAgents: 10,
    allowedFeatures: [
      'agents',
      'integrations',
      'calendar',
      'analytics',
      'testing',
      'cost-calculator',
      'knowledge',
      'sales',
    ],
    restrictedFeatures: ['mcp'],
    allowedPages: [
      '/',
      '/dashboard',
      '/agents',
      '/integrations',
      '/calendar',
      '/analytics',
      '/testing',
      '/cost-calculator',
      '/settings',
      '/knowledge',
      '/sales',
      '/sales/runs',
      '/workflows',
    ],
    restrictedPages: ['/mcp'],
  },
  'Bring Your Own (Enterprise)': {
    maxAgents: -1, // unlimited
    allowedFeatures: [
      'agents',
      'integrations',
      'calendar',
      'analytics',
      'testing',
      'mcp',
      'cost-calculator',
      'knowledge',
      'sales',
    ],
    restrictedFeatures: [],
    allowedPages: [
      '/',
      '/dashboard',
      '/agents',
      '/integrations',
      '/calendar',
      '/analytics',
      '/testing',
      '/mcp',
      '/cost-calculator',
      '/settings',
      '/knowledge',
      '/sales',
      '/sales/runs',
      '/workflows',
    ],
    restrictedPages: [],
  },
  'All‑In Managed': {
    maxAgents: -1, // unlimited
    allowedFeatures: [
      'agents',
      'integrations',
      'calendar',
      'analytics',
      'testing',
      'mcp',
      'cost-calculator',
      'knowledge',
      'sales',
    ],
    restrictedFeatures: ['settings_api_keys', 'settings_voip_key', 'settings_preferences'], // Hide configuration options - admin manages these
    allowedPages: [
      '/',
      '/dashboard',
      '/agents',
      '/integrations',
      '/calendar',
      '/analytics',
      '/testing',
      '/mcp',
      '/cost-calculator',
      '/settings',
      '/knowledge',
      '/sales',
      '/sales/runs',
      '/workflows',
    ],
    restrictedPages: [],
  },
};

// Role-based permissions
const ROLE_PERMISSIONS = {
  user: {
    allowedPages: [
      '/',
      '/dashboard',
      '/agents',
      '/integrations',
      '/calendar',
      '/analytics',
      '/testing',
      '/mcp',
      '/cost-calculator',
      '/settings',
      '/payment',
      '/knowledge',
      '/workflows',
      '/sales',
    ],
    restrictedPages: ['/user-management'],
    allowedFeatures: [
      'agents',
      'integrations',
      'calendar',
      'analytics',
      'testing',
      'mcp',
      'cost-calculator',
    ],
    restrictedFeatures: ['user-management'],
  },
  admin: {
    allowedPages: [
      '/',
      '/dashboard',
      '/agents',
      '/integrations',
      '/calendar',
      '/analytics',
      '/testing',
      '/mcp',
      '/cost-calculator',
      '/settings',
      '/user-management',
      '/knowledge',
      '/sales',
      '/sales/runs',
      '/workflows',
    ],
    restrictedPages: [],
    allowedFeatures: [
      'agents',
      'integrations',
      'calendar',
      'analytics',
      'testing',
      'mcp',
      'cost-calculator',
      'user-management',
      'knowledge',
      'sales',
    ],
    restrictedFeatures: [],
  },
  'super-admin': {
    allowedPages: [
      '/',
      '/dashboard',
      '/agents',
      '/integrations',
      '/calendar',
      '/analytics',
      '/testing',
      '/mcp',
      '/cost-calculator',
      '/settings',
      '/user-management',
      '/admin',
      '/knowledge',
      '/sales',
      '/sales/runs',
      '/workflows',
    ],
    restrictedPages: [],
    allowedFeatures: [
      'agents',
      'integrations',
      'calendar',
      'analytics',
      'testing',
      'mcp',
      'cost-calculator',
      'user-management',
      'admin',
      'knowledge',
      'sales',
    ],
    restrictedFeatures: [],
  },
};

const initialState = {
  planPermissions: null,
  rolePermissions: null,
  combinedPermissions: null,
  isLoading: false,
  error: null,
};

const permissionsSlice = createSlice({
  name: 'permissions',
  initialState,
  reducers: {
    setPlanPermissions: (state, action) => {
      const { planName, isTrial, maxAgents: maxAgentsOverride } = action.payload;
      const normalized = String(planName || '').trim();
      const isByok =
        /^BYOK$/i.test(normalized) || /bring\s*your\s*own/i.test(normalized);

      // BYOK must never fall through to Freemium (isTrial can be stale on user row)
      let base;
      if (isByok) {
        base = PLAN_PERMISSIONS.BYOK;
      } else if (normalized.includes('Freemium') || isTrial) {
        base = PLAN_PERMISSIONS['8-Day Freemium'];
      } else {
        base =
          PLAN_PERMISSIONS[normalized] ||
          PLAN_PERMISSIONS[planName] ||
          PLAN_PERMISSIONS['8-Day Freemium'];
      }

      // Per-user subscription_max_agents (admin-allocated) overrides plan default when set.
      const override =
        maxAgentsOverride != null && maxAgentsOverride !== ''
          ? Number(maxAgentsOverride)
          : null;
      state.planPermissions = {
        ...base,
        planName: planName || base.planName,
        maxAgents:
          override != null && !Number.isNaN(override) && override !== 0
            ? override
            : base.maxAgents,
      };

      state.combinedPermissions = null; // Will be recalculated
    },

    setRolePermissions: (state, action) => {
      const { role } = action.payload;
      state.rolePermissions = ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS['user'];
      state.combinedPermissions = null; // Will be recalculated
    },

    calculateCombinedPermissions: state => {
      if (state.planPermissions && state.rolePermissions) {
        // Combine plan and role permissions with safety checks
        const combined = {
          allowedPages: (state.rolePermissions.allowedPages || []).filter(page =>
            (state.planPermissions.allowedPages || []).includes(page)
          ),
          restrictedPages: [
            ...(state.rolePermissions.restrictedPages || []),
            ...(state.planPermissions.restrictedPages || []),
          ],
          allowedFeatures: (state.rolePermissions.allowedFeatures || []).filter(feature =>
            (state.planPermissions.allowedFeatures || []).includes(feature)
          ),
          restrictedFeatures: [
            ...(state.rolePermissions.restrictedFeatures || []),
            ...(state.planPermissions.restrictedFeatures || []),
          ],
          maxAgents: state.planPermissions.maxAgents,
          planName: state.planPermissions.planName,
        };

        state.combinedPermissions = combined;
      }
    },

    clearPermissions: state => {
      state.planPermissions = null;
      state.rolePermissions = null;
      state.combinedPermissions = null;
    },

    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },

    setError: (state, action) => {
      state.error = action.payload;
    },
  },
});

export const {
  setPlanPermissions,
  setRolePermissions,
  calculateCombinedPermissions,
  clearPermissions,
  setLoading,
  setError,
} = permissionsSlice.actions;

// Selectors
export const selectPlanPermissions = state => state.permissions.planPermissions;
export const selectRolePermissions = state => state.permissions.rolePermissions;
export const selectCombinedPermissions = state => state.permissions.combinedPermissions;
export const selectPermissionsLoading = state => state.permissions.isLoading;
export const selectPermissionsError = state => state.permissions.error;

// Helper selectors - Memoized to prevent infinite re-renders
export const selectCanAccessPage = createSelector(
  [state => state.permissions.combinedPermissions, (_, page) => page],
  (permissions, page) => {
    if (!permissions) return false;
    return permissions.allowedPages.includes(page);
  }
);

export const selectCanAccessFeature = createSelector(
  [state => state.permissions.combinedPermissions, (_, feature) => feature],
  (permissions, feature) => {
    if (!permissions) return false;
    return permissions.allowedFeatures.includes(feature);
  }
);

export const selectCanCreateAgent = state => {
  const permissions = state.permissions.combinedPermissions;
  // Prefer live subscription allocation (admin can raise this per user).
  const subscriptionMax = state.subscription?.current?.maxAgents;
  const maxAgents =
    subscriptionMax != null && subscriptionMax !== ''
      ? Number(subscriptionMax)
      : permissions?.maxAgents;

  if (maxAgents == null || Number.isNaN(maxAgents)) {
    // Permissions not ready — allow create attempt; API enforces the real limit.
    return true;
  }

  // Root / shared agents are assigned templates — they must not consume create quota.
  const currentAgentCount = (state.agents?.agents || []).filter(
    a => !(a?.isRootAgent || a?.is_root_agent)
  ).length;
  if (maxAgents === -1) return true; // unlimited
  return currentAgentCount < maxAgents;
};

export const selectMaxAgents = state => {
  const permissions = state.permissions.combinedPermissions;
  return permissions?.maxAgents || 0;
};

export const selectRestrictedPages = createSelector(
  [state => state.permissions.combinedPermissions],
  permissions => permissions?.restrictedPages || []
);

export const selectRestrictedFeatures = createSelector(
  [state => state.permissions.combinedPermissions],
  permissions => permissions?.restrictedFeatures || []
);

export default permissionsSlice.reducer;
